import * as React from 'react'

import { cn } from '@ps/ds/lib/utils'
import { Progress as ProgressPrimitive } from '@ps/ds/ui/progress'

export type ProgressProps = Omit<
  React.ComponentPropsWithoutRef<typeof ProgressPrimitive>,
  'value'
> & {
  // The current progress value. Should be between 0 and `max`.
  value: number
  // The maximum value for the progress. Must be greater than 0. Default is 100.
  max?: number
  // The thickness/height of the progress bar. Default is 'lg'.
  size?: 'xs' | 'sm' | 'md' | 'lg'
}

const sizeClasses: Record<NonNullable<ProgressProps['size']>, string> = {
  lg: 'h-4',
  md: 'h-3',
  sm: 'h-2',
  xs: 'h-1',
} as const

export const Progress = React.forwardRef<React.ElementRef<typeof ProgressPrimitive>, ProgressProps>(
  ({ className, value, max = 100, size = 'lg', ...props }, ref) => {
    const safeMax = typeof max === 'number' && max > 0 ? max : 100
    const clampedValue = Math.min(Math.max(value, 0), safeMax)
    const percent = (clampedValue / safeMax) * 100

    return (
      <ProgressPrimitive
        ref={ref}
        value={percent}
        className={cn(sizeClasses[size], 'bg-muted', '[&>div]:bg-orange-500', className)}
        {...props}
      />
    )
  },
)

Progress.displayName = 'Progress'
